import React, { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useOrderByIdQuery } from "../../Store/Apis/bookingApi";
import Loader from "../Loader/Loader";
import Error from "../Error/Error";
import formatDate from "../../Hooks/formatDate";
import CreditNoteModal from "./CreditNoteModal";

// Simplified clone of SalesDetails page focused on viewing order info + making credit note
const CreditNoteDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const [showCreditNoteModal, setShowCreditNoteModal] = useState(false);

  const orderByIdApi = useOrderByIdQuery(
    { orderID: orderId, outletID: user?.outletID },
    { skip: !orderId || !user?.outletID }
  );

  const orderData = orderByIdApi?.data?.data?.[0];
  const servicesRaw =
    orderData?.serviceTypeAndName || orderData?.services || [];

  // Normalize services so single customer and company bookings look the same
  const normalizedServices = useMemo(() => {
    return servicesRaw.map((s, idx) => {
      const qty = s.quantity || s.serviceQuantity || 1;
      const rate =
        s.rate || s.unitPrice || s.serviceRate || s.servicePrice || 0;
      const total =
        s.total || s.service_detail_total || Number(rate) * Number(qty);
      const creditedAmount = Number(
        s.creditedAmount !== undefined ? s.creditedAmount : 0
      );
      return {
        key: idx,
        serviceId: s.serviceID || s.serviceId || s.serviceTypeId || s.parentId,
        productServiceName:
          s.productServiceName || s.serviceName || s.itemName || "Service",
        serviceTypeName: s.service_type_name || s.serviceTypeName || "",
        guestName: s.subCustomerName || orderData.customerName || "-",
        guestEmail:
          s.subCustomerEmail ||
          orderData.emails ||
          orderData.customerEmail ||
          "",
        roomNo: s.roomNo || "",
        roomId: s.roomId || null,
        checkIn:
          s.subCustomerCheckIn || s.checkInDate || orderData.orderDate || null,
        checkOut:
          s.subCustomerCheckOut ||
          s.checkOutDate ||
          orderData.checkOutDate ||
          null,
        qty,
        rate: Number(rate),
        total: Number(total),
        creditedAmount,
        netTotal: Number(total) - creditedAmount,
        description: (() => {
          const rawDesc =
            s.description ||
            s.serviceDescription ||
            s.serviceTypeDescription ||
            s.service_type_name ||
            "";
          const norm = (val) => (val || "").toString().trim().toLowerCase();
          if (
            norm(rawDesc) === norm(s.service_type_name) ||
            norm(rawDesc) === norm(s.serviceTypeName) ||
            norm(rawDesc) === norm(s.productServiceName) ||
            norm(rawDesc) === norm(s.serviceName)
          ) {
            return ""; // suppress duplicate
          }
          return rawDesc;
        })(),
      };
    });
  }, [servicesRaw, orderData]);

  // Aggregate totals for summary
  const originalSubtotal = useMemo(
    () => normalizedServices.reduce((acc, s) => acc + s.total, 0),
    [normalizedServices]
  );
  const totalCredited = useMemo(
    () =>
      normalizedServices.reduce((acc, s) => acc + (s.creditedAmount || 0), 0),
    [normalizedServices]
  );

  const paidTotal = Number(orderData?.amountPaid) || 0;
  // Overpayment is calculated as amountPaid - subTotal
  const overpaymentBase = paidTotal - Number(orderData?.subTotal || 0);
  // Remaining overpayment after subtracting credits already issued
  const orderAmountOwed = overpaymentBase - totalCredited; // can go negative (still owing) or positive (remaining overpayment)
  // Net subtotal reflects credits already applied
  const netSubtotal = paidTotal - totalCredited;

  // Derive a representative Rate for the top summary:
  // Priority: explicit orderData.rate -> single service rate -> weighted average (total / qty)
  const headerRate = useMemo(() => {
    if (orderData?.rate && !isNaN(orderData.rate))
      return Number(orderData.rate);
    if (normalizedServices.length === 1) return normalizedServices[0].rate;
    if (normalizedServices.length > 1) {
      const totalAmount = normalizedServices.reduce((a, s) => a + s.total, 0);
      const totalQty = normalizedServices.reduce(
        (a, s) => a + (Number(s.qty) || 0),
        0
      );
      if (totalQty > 0) return totalAmount / totalQty;
    }
    return 0;
  }, [orderData?.rate, normalizedServices]);

  if (orderByIdApi.isLoading) return <Loader />;
  if (orderByIdApi.isError) return <Error />;
  if (!orderData)
    return <div className="text-center py-4">Order not found</div>;

  return (
    <div>
      <div className="row align-items-center justify-content-between mb-4">
        <div className="col">
          <h5 className="fw-500 text-white mb-0">
            Order Details - #{orderData.orderNumber || orderData.orderId}
          </h5>
        </div>
        <div className="col-auto">
          <button
            className="btn btn-sm btn-secondary me-2"
            onClick={() => navigate("/credit/credit-note")}
          >
            Back
          </button>
          <button
            className="btn btn-sm btn-warning"
            onClick={() => setShowCreditNoteModal(true)}
            disabled={orderAmountOwed <= 0}
            title={orderAmountOwed <= 0 ? "No amount owed to credit" : ""}
          >
            Make Credit Note
          </button>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card mb-4">
            <div className="card-header p-4">
              <div className="row">
                <div className="col-md-4">
                  <p className="text-sm mb-1">
                    <span className="me-2 fw-600">Customer:</span>
                    <span>{orderData.customerName}</span>
                  </p>
                  <p className="text-sm mb-1">
                    <span className="me-2 fw-600">Phone:</span>
                    <span>{orderData.customerNumber || "N/A"}</span>
                  </p>
                  <p className="text-sm mb-0">
                    <span className="me-2 fw-600">Outlet:</span>
                    <span>{orderData.outletName}</span>
                  </p>
                </div>
                <div className="col-md-4">
                  <p className="text-sm mb-1">
                    <span className="me-2 fw-600">Check In:</span>
                    <span>{formatDate(orderData.orderDate)}</span>
                  </p>
                  <p className="text-sm mb-1">
                    <span className="me-2 fw-600">Check Out:</span>
                    <span>{formatDate(orderData.checkOutDate)}</span>
                  </p>
                  <p className="text-sm mb-0">
                    <span className="me-2 fw-600">Status:</span>
                    <span className="badge badge-sm bg-gradient-primary text-uppercase">
                      {orderData.statusDescription}
                    </span>
                  </p>
                </div>
                <div className="col-md-4">
                  <p className="text-sm mb-1">
                    <span className="me-2 fw-600">Sub Total:</span>
                    <span>R {Number(orderData.subTotal).toFixed(2)}</span>
                  </p>
                  <p className="text-sm mb-1">
                    <span className="me-2 fw-600">Paid:</span>
                    <span>R {Number(orderData.amountPaid).toFixed(2)}</span>
                  </p>
                  <p className="text-sm mb-0">
                    <span className="me-2 fw-600">Rate:</span>
                    <span>R {headerRate.toFixed(2)}</span>
                  </p>
                </div>
              </div>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table mb-0">
                  <thead>
                    <tr>
                      <th>Service</th>
                      <th>Guest / Room</th>
                      <th>Check In</th>
                      <th>Check Out</th>
                      <th className="text-center">Qty</th>
                      <th className="text-end">SubTotal</th>
                      <th className="text-end">Paid Total</th>
                      <th className="text-end text-danger">Credit</th>
                      <th className="text-end text-danger">Amount Owed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {normalizedServices.map((s) => {
                      // Keep previous pro-rata paid distribution for Paid Total column
                      const linePaid = originalSubtotal
                        ? paidTotal * (s.total / originalSubtotal)
                        : 0;
                      // Amount owed per instruction uses order-level formula (paid - subtotal)
                      const lineAmountOwed = orderAmountOwed;
                      return (
                        <tr key={s.key}>
                          <td>
                            <span className="fw-600">
                              {s.productServiceName}
                            </span>
                            {s.serviceTypeName && (
                              <div className="small text-muted">
                                {s.serviceTypeName}
                              </div>
                            )}
                            {s.description && (
                              <div className="small text-muted">
                                {s.description}
                              </div>
                            )}
                          </td>
                          <td className="small">
                            <div>{s.guestName}</div>
                            {(s.roomNo || s.roomId) && (
                              <div className="text-muted">
                                Room {s.roomNo || s.roomId}
                              </div>
                            )}
                          </td>
                          <td className="small">{formatDate(s.checkIn)}</td>
                          <td className="small">{formatDate(s.checkOut)}</td>
                          <td className="text-center">{s.qty}</td>
                            <td className="text-end">R {Number(orderData.subTotal).toFixed(2)}</td>
                          <td className="text-end">R {linePaid.toFixed(2)}</td>
                          <td className="text-end text-danger">
                            {s.creditedAmount > 0
                              ? `- R ${s.creditedAmount.toFixed(2)}`
                              : "-"}
                          </td>
                          <td className="text-end text-danger fw-600">
                            R {lineAmountOwed.toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}
                    {normalizedServices.length === 0 && (
                      <tr>
                        <td
                          colSpan={9 - 1}
                          className="text-center py-4 text-muted"
                        >
                          No services/items on this order
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="card-footer">
              <div className="row">
                <div className="col-md-8"></div>
                <div className="col-md-4">
                  <div className="card bg-light shadow-none mb-0">
                    <div className="card-body py-3">
                      <div className="d-flex justify-content-between mb-1">
                        <span>Paid Total</span>
                        <span>R {paidTotal.toFixed(2)}</span>
                      </div>

                      <div className="d-flex justify-content-between mb-1 text-danger">
                        <span>Total Credit</span>
                        <span>- R {totalCredited.toFixed(2)}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-1 fw-600">
                        <span>Net Sub Total</span>
                        <span>R {netSubtotal.toFixed(2)}</span>
                      </div>

                      <hr className="my-2" />
                      <div
                        className={`d-flex justify-content-between fw-600 ${
                          orderAmountOwed < 0 ? "text-danger" : "text-success"
                        }`}
                      >
                        <span>
                          {orderAmountOwed < 0
                            ? "Amount Owed"
                            : "Overpayment Remaining"}
                        </span>
                        <span>R {Math.abs(orderAmountOwed).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CreditNoteModal
        show={showCreditNoteModal}
        onHide={() => setShowCreditNoteModal(false)}
        onCreditSuccess={() => {
          // Refetch order so credited amounts & totals update in real time
          if (orderByIdApi?.refetch) orderByIdApi.refetch();
        }}
        order={{
          orderID: orderData.orderId,
          customerID: orderData.customerID,
          customerName: orderData.customerName,
          paidTotal: paidTotal,
          amountOwedRaw: overpaymentBase, // pass base overpayment before subtracting already credited
          outletID: orderData.outletID || orderData.outletId || user?.outletID,
          orderDetails: normalizedServices.map((s) => ({
            serviceID: s.serviceId,
            serviceName: s.productServiceName,
            quantity: s.qty,
            rate: s.rate,
            description: s.description,
            guestName: s.guestName,
            checkIn: s.checkIn,
            checkOut: s.checkOut,
            roomId: s.roomId,
            total: s.total, // original line total before credit
            creditedAmount: s.creditedAmount || 0, // pass previous credited for modal
          })),
        }}
      />
    </div>
  );
};

export default CreditNoteDetail;
